import { useEffect, useMemo, useState } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { useAuth } from "@/context/AuthContext";
import { Search, Eye, Power, Building2 } from "lucide-react";
import { PaginationBar, usePagination } from "@/components/Pagination";

function fmtDate(iso) {
  if (!iso) return "—";
  try { return new Date(iso).toLocaleDateString("en-IN", { day: "2-digit", month: "short", year: "numeric" }); }
  catch { return "—"; }
}

function DetailRow({ label, value }) {
  return (
    <div>
      <div className="text-[10px] uppercase tracking-wider text-muted-foreground">{label}</div>
      <div className="text-sm font-medium mt-0.5">{value || "—"}</div>
    </div>
  );
}

export default function ActiveOrganisations() {
  const { user } = useAuth();
  const isSuper = (user?.platform_role || "super_admin") === "super_admin";
  const [tenants, setTenants] = useState([]);
  const [search, setSearch] = useState("");
  const [planFilter, setPlanFilter] = useState("all");
  const [statusFilter, setStatusFilter] = useState("active");
  const [details, setDetails] = useState(null);
  const [deactivating, setDeactivating] = useState(null);
  const [busy, setBusy] = useState(false);

  const load = () => api.get("/admin/tenants").then(r => setTenants(r.data))
    .catch(e => toast.error(e.response?.data?.detail || "Failed to load organisations"));
  useEffect(() => { load(); }, []);

  const active = useMemo(() => tenants.filter(t => t.status === "active"), [tenants]);
  const planOptions = useMemo(() =>
    [...new Set(active.map(t => t.plan_name || "__none__"))].sort(), [active]);

  const filtered = useMemo(() => {
    if (statusFilter === "inactive") return [];
    return active.filter(t => {
      if (search && !t.business_name?.toLowerCase().includes(search.toLowerCase())) return false;
      if (planFilter !== "all" && (t.plan_name || "__none__") !== planFilter) return false;
      return true;
    });
  }, [active, search, planFilter, statusFilter]);
  const pg = usePagination(filtered, JSON.stringify([search, statusFilter]));

  const deactivate = async () => {
    if (!deactivating) return;
    setBusy(true);
    try {
      await api.post(`/admin/tenants/${deactivating.id}/deactivate`);
      toast.success(`${deactivating.business_name} deactivated — its users can no longer sign in`);
      setDeactivating(null);
      load();
    } catch (e) { toast.error(e.response?.data?.detail || "Deactivate failed"); }
    finally { setBusy(false); }
  };

  return (
    <div className="p-6 space-y-6" data-testid="active-orgs-page">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h1 className="text-3xl font-bold">Active Organisations</h1>
          <p className="text-muted-foreground">Currently live tenants on the platform.</p>
        </div>
        <Badge variant="outline" className="text-sm" data-testid="ao-count">{filtered.length} of {active.length} shown</Badge>
      </div>

      <Card>
        <CardContent className="p-4">
          <div className="flex gap-3 flex-wrap items-end">
            <div className="flex-1 min-w-56">
              <Label className="text-xs uppercase tracking-wider text-muted-foreground">Search</Label>
              <div className="relative mt-1">
                <Search className="w-4 h-4 absolute left-2.5 top-2.5 text-muted-foreground"/>
                <Input data-testid="ao-search" value={search} onChange={(e) => setSearch(e.target.value)}
                  placeholder="Search organisation name…" className="pl-8"/>
              </div>
            </div>
            <div>
              <Label className="text-xs uppercase tracking-wider text-muted-foreground">Plan</Label>
              <Select value={planFilter} onValueChange={setPlanFilter}>
                <SelectTrigger data-testid="ao-filter-plan" className="mt-1 w-48"><SelectValue/></SelectTrigger>
                <SelectContent>
                  <SelectItem data-testid="ao-plan-all" value="all">All Plans</SelectItem>
                  {planOptions.map(p => (
                    <SelectItem key={p} data-testid={`ao-plan-${p}`} value={p}>{p === "__none__" ? "— No plan —" : p}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-xs uppercase tracking-wider text-muted-foreground">Status</Label>
              <Select value={statusFilter} onValueChange={setStatusFilter}>
                <SelectTrigger data-testid="ao-filter-status" className="mt-1 w-40"><SelectValue/></SelectTrigger>
                <SelectContent>
                  <SelectItem data-testid="ao-status-active" value="active">Active</SelectItem>
                  <SelectItem data-testid="ao-status-inactive" value="inactive">Inactive</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardContent className="p-0">
          <Table data-testid="ao-table">
            <TableHeader>
              <TableRow>
                <TableHead>Organisation Name</TableHead>
                <TableHead>Plan</TableHead>
                <TableHead>Establishments</TableHead>
                <TableHead>Active Since</TableHead>
                <TableHead>Status</TableHead>
                <TableHead className="text-right">Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {pg.pageItems.map(t => (
                <TableRow key={t.id} data-testid={`ao-row-${t.id}`}>
                  <TableCell className="font-medium">{t.business_name}</TableCell>
                  <TableCell>{t.plan_name ? <Badge variant="outline">{t.plan_name}</Badge> : <span className="text-xs text-muted-foreground">—</span>}</TableCell>
                  <TableCell className="font-mono text-xs">{t.establishment_count ?? 0}</TableCell>
                  <TableCell className="text-xs text-muted-foreground">{fmtDate(t.approved_at || t.created_at)}</TableCell>
                  <TableCell><Badge data-testid={`ao-status-badge-${t.id}`}>Active</Badge></TableCell>
                  <TableCell className="text-right space-x-1">
                    <Button size="sm" variant="outline" onClick={() => setDetails(t)} data-testid={`ao-view-${t.id}`}>
                      <Eye className="w-3.5 h-3.5 mr-1"/> View Details
                    </Button>
                    {isSuper && (
                      <Button size="sm" variant="destructive" onClick={() => setDeactivating(t)} data-testid={`ao-deactivate-${t.id}`}>
                        <Power className="w-3.5 h-3.5 mr-1"/> Deactivate
                      </Button>
                    )}
                  </TableCell>
                </TableRow>
              ))}
              {!filtered.length && (
                <TableRow>
                  <TableCell colSpan={6} className="text-center text-muted-foreground py-8" data-testid="ao-empty">
                    {statusFilter === "inactive"
                      ? "Deactivated organisations can be reactivated from the Platform Overview page."
                      : active.length ? "No organisations match the current search/filters." : "No active organisations"}
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
          <PaginationBar pg={pg}/>
        </CardContent>
      </Card>

      <Dialog open={!!details} onOpenChange={(v) => { if (!v) setDetails(null); }}>
        <DialogContent className="max-w-2xl" data-testid="ao-details-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2"><Building2 className="w-4 h-4"/> {details?.business_name}</DialogTitle>
            <DialogDescription>Organisation profile and platform status.</DialogDescription>
          </DialogHeader>
          {details && (
            <div className="space-y-5">
              <div className="grid grid-cols-3 gap-4">
                <DetailRow label="GSTIN" value={details.gstin}/>
                <DetailRow label="Phone" value={details.phone}/>
                <DetailRow label="Email" value={details.email || details.contact_email}/>
                <DetailRow label="Address" value={details.address}/>
                <DetailRow label="Plan" value={details.plan_name}/>
                <DetailRow label="Establishments" value={String(details.establishment_count ?? 0)}/>
                <DetailRow label="Registered" value={fmtDate(details.created_at)}/>
                <DetailRow label="Active Since" value={fmtDate(details.approved_at || details.created_at)}/>
                <DetailRow label="Status" value="Active"/>
              </div>
              <div>
                <div className="text-[10px] uppercase tracking-wider text-muted-foreground mb-2">Owners</div>
                {(details.owners || []).length ? (
                  <Table>
                    <TableHeader>
                      <TableRow><TableHead>Name</TableHead><TableHead>Email</TableHead><TableHead>Status</TableHead></TableRow>
                    </TableHeader>
                    <TableBody>
                      {details.owners.map(o => (
                        <TableRow key={o.id} data-testid={`ao-owner-${o.id}`}>
                          <TableCell className="text-sm">{o.name}</TableCell>
                          <TableCell className="text-sm">{o.email}</TableCell>
                          <TableCell><Badge variant={o.is_active ? "default" : "destructive"}>{o.is_active ? "Active" : "Inactive"}</Badge></TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                ) : <div className="text-xs text-muted-foreground">No owners found</div>}
              </div>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setDetails(null)} data-testid="ao-details-close">Close</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={!!deactivating} onOpenChange={(v) => { if (!v) setDeactivating(null); }}>
        <DialogContent className="max-w-md" data-testid="ao-deactivate-dialog">
          <DialogHeader>
            <DialogTitle>Deactivate · {deactivating?.business_name}</DialogTitle>
            <DialogDescription>
              The organisation becomes Inactive and all its users are blocked from signing in.
              You can restore it any time with Reactivate from the Platform Overview page.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDeactivating(null)} data-testid="ao-deactivate-cancel">Cancel</Button>
            <Button variant="destructive" onClick={deactivate} disabled={busy} data-testid="ao-deactivate-confirm">
              {busy ? "Deactivating…" : "Deactivate Organisation"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
